// ═══════════════════════════════════════════════════════════════
// ModuleNav — Responsive module sub-navigation
// Desktop: CDS tab bar for ungrouped items, dropdown for groups.
// Mobile: single hamburger menu.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Badge } from '../gab/GabComponents';
import { IconMenu2, IconChevronDown } from '@tabler/icons-react';
import { brand } from '../../lib/colors';
import type { NavItem } from '../../types/nav';

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface Props {
  items?: NavItem[];
  groups?: NavGroup[];
  accentColor?: string;
  extra?: React.ReactNode;
}

// Dropdown for a named group of nav items
function GroupDropdown({ group, pathname, navigate, color }: {
  group: NavGroup;
  pathname: string;
  navigate: (path: string) => void;
  color: string;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isGroupActive = group.items.some(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );
  const activeItem = group.items.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  function openDropdown() {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  }
  function scheduleClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  }

  return (
    <div style={{ position: 'relative' }} onMouseEnter={openDropdown} onMouseLeave={scheduleClose}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`tab${isGroupActive ? ' active' : ''}`}
        style={{ gap: 5 }}
      >
        {activeItem ? (
          <>
            <activeItem.icon size={14} />
            <span>{activeItem.label}</span>
            <span style={{ opacity: 0.55, fontSize: 12 }}>({group.label})</span>
          </>
        ) : (
          <span>{group.label}</span>
        )}
        <IconChevronDown size={11} style={{ marginLeft: 1, opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: '#fff', border: '1px solid #e2e4e8',
          borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          minWidth: 200, overflow: 'hidden',
        }}>
          {group.items.map(item => {
            const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '9px 16px', fontSize: 14,
                  background: active ? `${color}18` : 'transparent',
                  color: active ? color : '#333',
                  fontWeight: active ? 600 : 400,
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  borderLeft: active ? `3px solid ${color}` : '3px solid transparent',
                }}
              >
                <item.icon size={14} />
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ModuleNav({ items = [], groups, accentColor, extra }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const color = accentColor || brand.blurple;

  const activeItem = items.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  const renderDesktop = () => {
    if (groups && groups.length > 0) {
      return groups.map((group, gi) => {
        // Unnamed group → render items as tabs
        if (!group.label) {
          return (
            <React.Fragment key={gi}>
              {group.items.map(item => {
                const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
                return (
                  <button
                    key={item.path}
                    className={`tab${active ? ' active' : ''}`}
                    onClick={() => navigate(item.path)}
                  >
                    <item.icon size={14} />
                    {item.label}
                  </button>
                );
              })}
              {/* Divider after unnamed group if named groups follow */}
              {gi < groups.length - 1 && groups[gi + 1]?.label && (
                <div style={{ width: 1, height: 20, background: 'var(--color-neutral-200)', margin: '0 4px', alignSelf: 'center' }} />
              )}
            </React.Fragment>
          );
        }
        // Named group → dropdown
        return (
          <GroupDropdown
            key={gi}
            group={group}
            pathname={pathname}
            navigate={navigate}
            color={color}
          />
        );
      });
    }

    // Flat (no groups)
    return items.map(item => {
      const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
      return (
        <button
          key={item.path}
          className={`tab${active ? ' active' : ''}`}
          onClick={() => navigate(item.path)}
        >
          <item.icon size={14} />
          {item.label}
          {item.badge ? <Badge size="xs" circle color="var(--color-error)" variant="filled">{item.badge}</Badge> : null}
        </button>
      );
    });
  };

  const renderMenuItems = () => {
    if (groups && groups.length > 0) {
      return groups.map((group, gi) => (
        <div key={gi}>
          {gi > 0 && <Menu.Divider />}
          {group.label && <Menu.Label>{group.label}</Menu.Label>}
          {group.items.map(item => {
            const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
            return (
              <Menu.Item
                key={item.path}
                leftSection={<item.icon size={14} />}
                onClick={() => navigate(item.path)}
                style={active ? { backgroundColor: 'var(--color-primary-50)', color: 'var(--color-primary-800)', fontWeight: 600 } : undefined}
              >
                {item.label}
              </Menu.Item>
            );
          })}
        </div>
      ));
    }
    return items.map(item => {
      const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
      return (
        <Menu.Item
          key={item.path}
          leftSection={<item.icon size={14} />}
          rightSection={item.badge ? <Badge size="xs" circle color="var(--color-error)" variant="filled">{item.badge}</Badge> : undefined}
          onClick={() => navigate(item.path)}
          style={active ? { backgroundColor: 'var(--color-primary-50)', color: 'var(--color-primary-800)', fontWeight: 600 } : undefined}
        >
          {item.label}
        </Menu.Item>
      );
    });
  };

  return (
    <>
      {/* Desktop: CDS tab bar — hidden on mobile via CSS */}
      <div className="module-nav-desktop" style={{
        display: 'flex', alignItems: 'center', gap: 0,
        borderBottom: '1px solid var(--color-neutral-200)',
        marginBottom: 'var(--spacing-4)',
        flexWrap: 'wrap',
      }}>
        {renderDesktop()}
        {extra && <div style={{ marginLeft: 'auto' }}>{extra}</div>}
      </div>

      {/* Mobile: hamburger menu — hidden on desktop via CSS */}
      <div className="module-nav-mobile" style={{
        alignItems: 'center', gap: 8,
        borderBottom: '1px solid var(--frost)', paddingBottom: 8, marginBottom: 'var(--spacing-4)',
      }}>
        <Menu shadow="lg" width={250} position="bottom-start" withArrow>
          <Menu.Target>
            <button className="btn btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <IconMenu2 size={16} />
              {activeItem?.label || 'Navigate'}
            </button>
          </Menu.Target>
          <Menu.Dropdown style={{ maxHeight: 400, overflowY: 'auto' }}>
            {renderMenuItems()}
          </Menu.Dropdown>
        </Menu>
        {extra && <div style={{ marginLeft: 'auto' }}>{extra}</div>}
      </div>
    </>
  );
}
